import { html, LitElement, TemplateResult } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { WEB_COMPONENT_NETWORK_IOC_SCAN } from '../../../../../webcomponents.constants';
import { FontSizes, FontWeights } from '../../../../../types/common/types';
import { GzInputWebComponent } from '../../../../../ui/gzInput/component/GzInputWebComponent';
import {
  Indicator,
  IndicatorConfig,
  IndicatorsGroupedByType,
  IndicatorTypes,
  IocEvents,
  IocScanRequestRules,
} from './types';
import { styles } from './view/styles';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { Subscription } from 'rxjs';
import { TaskTarget } from '../../../types';
import { getFormattedDateForTask } from '../../../../networkAppContainer/helpers/utils';
import { ProtectedEntitiesHelper } from '../../../../networkAppContainer/helpers/protectedEntities/protected.entities.helper';
import { translateFn } from '../../../../../utils/translator.helper';

@customElement(WEB_COMPONENT_NETWORK_IOC_SCAN)
export class GzIocScanWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  public targets: Array<TaskTarget>;

  @state()
  private subscription: Subscription | null;

  @query('#ioc-scan-container')
  private iocScanContainer: HTMLDivElement;

  @query('#scan-name-field')
  private scanNameField: GzFormFieldWebComponent<GzInputWebComponent>;

  private indicatorsToScanFor: Array<Indicator> = [];

  private nameFieldIsValid: boolean;

  private indicatorsConfigs: Array<IndicatorConfig> = [
    {
      indicatorType: IndicatorTypes.MD5,
      checkboxLabel: translateFn('MD5'),
      mainLabel: translateFn('Add MD5 indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^[a-zA-Z0-9]{32}$',
      inputMaxChars: 5000,
      hintText: translateFn(
        'Each entry must be 32 characters long. Use only alphanumeric characters. Each entry must be unique.'
      ),
      requiresExtraValidations: true,
      entryMinLength: 32,
    },
    {
      indicatorType: IndicatorTypes.SHA1,
      checkboxLabel: translateFn('SHA1'),
      mainLabel: translateFn('Add SHA1 indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^[a-zA-Z0-9]{40}$',
      inputMaxChars: 5000,
      hintText: translateFn(
        'Each entry must be 40 characters long. Use only alphanumeric characters. Each entry must be unique.'
      ),
      requiresExtraValidations: true,
      entryMinLength: 40,
    },
    {
      indicatorType: IndicatorTypes.SHA256,
      checkboxLabel: translateFn('SHA256'),
      mainLabel: translateFn('Add SHA256 indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^[a-zA-Z0-9]{64}$',
      inputMaxChars: 5000,
      hintText: translateFn(
        'Each entry must be 64 characters long. Use only alphanumeric characters. Each entry must be unique.'
      ),
      requiresExtraValidations: true,
      entryMinLength: 64,
    },
    {
      indicatorType: IndicatorTypes.SHA512,
      checkboxLabel: translateFn('SHA512'),
      mainLabel: translateFn('Add SHA512 indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^[a-zA-Z0-9]{128}$',
      inputMaxChars: 5000,
      hintText: translateFn(
        'Each entry must be 128 characters long. Use only alphanumeric characters. Each entry must be unique.'
      ),
      requiresExtraValidations: true,
      entryMinLength: 128,
    },
    {
      indicatorType: IndicatorTypes.FILE_NAME,
      checkboxLabel: translateFn('File names'),
      mainLabel: translateFn('Add file name indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^.+$',
      inputMaxChars: 5000,
      hintText: translateFn('Each entry must be unique.'),
      requiresExtraValidations: false,
    },
    {
      indicatorType: IndicatorTypes.PROCESS_NAME,
      checkboxLabel: translateFn('Process names'),
      mainLabel: translateFn('Add process name indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^.+$',
      inputMaxChars: 5000,
      hintText: translateFn('Each entry must be unique.'),
      requiresExtraValidations: false,
    },
    {
      indicatorType: IndicatorTypes.REGISTRY_VALUE,
      checkboxLabel: translateFn('Registry values'),
      mainLabel: translateFn('Add registry value indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^.+$',
      inputMaxChars: 5000,
      hintText: translateFn('Each entry must be unique.'),
      requiresExtraValidations: false,
    },
    {
      indicatorType: IndicatorTypes.REGISTRY_KEY,
      checkboxLabel: translateFn('Registry keys'),
      mainLabel: translateFn('Add registry key indicators*:'),
      textareaPlaceholder: translateFn('Specify indicators here. Separate them by comma or press Enter.'),
      inputValidationRegex: '^.+$',
      inputMaxChars: 5000,
      hintText: translateFn('Each entry must be unique.'),
      requiresExtraValidations: false,
    },
  ];

  public getRequestData(): IocScanRequestRules {
    return {
      name: this.scanNameField.value as string,
      indicators: this.groupSetIndicatorsByType(),
    };
  }

  private groupSetIndicatorsByType(): Array<IndicatorsGroupedByType> {
    const indicatorsGroupedByType: Array<{ type: IndicatorTypes; data: Array<string> }> = [
      {
        type: IndicatorTypes.MD5,
        data: [],
      },
      {
        type: IndicatorTypes.SHA1,
        data: [],
      },
      {
        type: IndicatorTypes.SHA256,
        data: [],
      },
      {
        type: IndicatorTypes.SHA512,
        data: [],
      },
      {
        type: IndicatorTypes.FILE_NAME,
        data: [],
      },
      {
        type: IndicatorTypes.PROCESS_NAME,
        data: [],
      },
      {
        type: IndicatorTypes.REGISTRY_KEY,
        data: [],
      },
      {
        type: IndicatorTypes.REGISTRY_VALUE,
        data: [],
      },
    ];

    for (const indicator of this.indicatorsToScanFor) {
      const category = indicatorsGroupedByType.filter(indicatorData => indicatorData.type === indicator.type)[0];
      category.data.push(indicator.value);
    }

    return indicatorsGroupedByType.filter(indicator => indicator.data.length > 0);
  }

  private initScanNameField(): void {
    this.scanNameField.value = `IOC scan ${getFormattedDateForTask()}`;

    this.scanNameField.Validators = [RequiredValidator(translateFn(`Task name is required.`))];
    this.subscription = this.scanNameField.valid$.subscribe((valid: boolean) => {
      this.nameFieldIsValid = valid;

      this.toggleSaveButtonEnabling();
    });
  }

  firstUpdated() {
    this.initScanNameField();

    this.iocScanContainer.addEventListener(IocEvents.INDICATOR_ADDED, this.addIndicator.bind(this));
    this.iocScanContainer.addEventListener(IocEvents.INDICATOR_REMOVED, this.removeIndicator.bind(this));
    this.iocScanContainer.addEventListener(IocEvents.CATEGORY_TOGGLED, this.toggleIndicatorCategory.bind(this));
  }

  disconnectedCallback() {
    this.subscription?.unsubscribe();
  }

  private toggleIndicatorCategory(event: CustomEvent) {
    event.stopPropagation();
    const { type, checked, values } = event.detail;

    if (!values.length) {
      return;
    }

    if (checked) {
      const newIndicatorsToScanFor: Array<Indicator> = values.map(value => ({ type, value }));
      this.indicatorsToScanFor = this.indicatorsToScanFor.concat(newIndicatorsToScanFor);
    } else {
      this.indicatorsToScanFor = this.indicatorsToScanFor.filter(indicator => indicator.type !== type);
    }

    this.toggleSaveButtonEnabling();
  }

  private addIndicator(event: CustomEvent) {
    event.stopPropagation();
    const newValues = event.detail.values.map(value => ({ value, type: event.detail.type }));

    this.indicatorsToScanFor = this.indicatorsToScanFor.concat(newValues);
    this.toggleSaveButtonEnabling();
  }

  private removeIndicator(event: CustomEvent) {
    event.stopPropagation();

    this.indicatorsToScanFor = this.indicatorsToScanFor.filter(
      setIndicator => setIndicator.value !== event.detail.value
    );

    this.toggleSaveButtonEnabling();
  }

  private toggleSaveButtonEnabling(): void {
    this.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: this.indicatorsToScanFor.length === 0 || !this.nameFieldIsValid },
      })
    );
  }

  render(): TemplateResult {
    return html`
      <div id="ioc-scan-container" class="container">
        <gz-title fontSize="${FontSizes.BODY}" fontWeight="${FontWeights.MEDIUM}" class="title-container"
          >${translateFn('General')}</gz-title
        >
        <gz-form-field label="${translateFn('Selected targets')}" id="selectedDevicesLabel" disabled>
          <gz-chips-generator-input
            gz-form-input
            .chipList="${this.targets.map(target => {
              return ProtectedEntitiesHelper.isEndpoint(target)
                ? `${target.name}`
                : `${target.company.name}-${target.name}`;
            })}"
            ?themeInput="${true}"
            ?hideCopyToClipboardButton="${true}"
            ?allowDuplicates="${true}"
            required
            disabled
          ></gz-chips-generator-input>
        </gz-form-field>
        <gz-form-field label="${translateFn('Task name')}" id="scan-name-field" class="mb-4">
          <gz-input
            gz-form-input
            type="text"
            id="scan-name-input"
            class="scan-name-input"
            placeholder="${translateFn('Specify task name')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-title fontSize="${FontSizes.BODY}" fontWeight="${FontWeights.MEDIUM}" class="title-container"
          >${translateFn('Indicators of compromise')}</gz-title
        >
        <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.NORMAL}" class="mb-3"
          >${translateFn('Select at least one indicator for endpoint scanning:')}</gz-text
        >
        ${map(this.indicatorsConfigs, (indicatorConfig: IndicatorConfig, index: number) => {
          return html`<gz-ioc-scan-indicator
            indicatorType="${indicatorConfig.indicatorType}"
            checkboxLabel="${indicatorConfig.checkboxLabel}"
            ?checkedByDefault="${index === 0}"
            mainLabel="${indicatorConfig.mainLabel}"
            textareaPlaceholder="${indicatorConfig.textareaPlaceholder}"
            inputValidationRegex="${indicatorConfig.inputValidationRegex}"
            .inputMaxChars="${indicatorConfig.inputMaxChars}"
            hintText="${indicatorConfig.hintText}"
            notUniqueErrorMessage="${translateFn('Each entry must be unique.')}"
            invalidItemErrorMessage="${indicatorConfig.requiresExtraValidations
              ? translateFn(`Each entry must be {length} characters long. Use only alphanumeric characters.`, {
                  length: indicatorConfig.entryMinLength,
                })
              : undefined}"
          />`;
        })}
      </div>
    `;
  }
}
